import { create } from 'zustand';
import { api } from '../api/client';
import { useAuth, isAdmin } from '../store/auth';
import type { Lesson, LessonProgressEntry } from './types';

/**
 * Global lesson state. The overlay engine (LessonOverlay) renders from this;
 * the Lessons page starts lessons and reads progress. Active-lesson state is
 * in-memory only: a full page reload aborts the lesson (the overlay can't
 * restore half-finished form state), which is communicated on the Lessons page.
 */
interface LessonStore {
  active: Lesson | null;
  stepIndex: number;
  /** lessonId -> progress entry, loaded from the server. */
  progress: Record<string, LessonProgressEntry>;
  progressLoaded: boolean;

  loadProgress: () => Promise<void>;
  start: (lesson: Lesson) => void;
  advance: () => void;
  skip: () => void;
  exit: () => void;
}

async function putProgress(lessonId: string, status: 'in_progress' | 'completed') {
  try {
    await api.put(`/lessons/progress/${lessonId}`, { status });
  } catch {
    // Progress tracking is best-effort; never let it break a running lesson.
  }
}

export const useLessons = create<LessonStore>((set, get) => ({
  active: null,
  stepIndex: 0,
  progress: {},
  progressLoaded: false,

  loadProgress: async () => {
    try {
      const rows = await api.get<LessonProgressEntry[]>('/lessons/progress');
      set({
        progress: Object.fromEntries(rows.map((r) => [r.lessonId, r])),
        progressLoaded: true,
      });
    } catch {
      set({ progressLoaded: true });
    }
  },

  start: (lesson) => {
    // Drop admin-only steps for non-admin users so step counts stay honest.
    const admin = isAdmin(useAuth.getState().me?.role);
    const steps = lesson.steps.filter((s) => !s.adminOnly || admin);
    set({ active: { ...lesson, steps }, stepIndex: 0 });
    void putProgress(lesson.id, 'in_progress');
    set((s) => ({
      progress: {
        ...s.progress,
        [lesson.id]: { lessonId: lesson.id, status: 'in_progress', completedAt: null, updatedAt: new Date().toISOString() },
      },
    }));
  },

  advance: () => {
    const { active, stepIndex } = get();
    if (!active) return;
    if (stepIndex + 1 >= active.steps.length) {
      // Finished the last step.
      void putProgress(active.id, 'completed');
      set((s) => ({
        active: null,
        stepIndex: 0,
        progress: {
          ...s.progress,
          [active.id]: { lessonId: active.id, status: 'completed', completedAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
        },
      }));
      return;
    }
    set({ stepIndex: stepIndex + 1 });
  },

  // Skip is identical to advance but kept separate so optional steps can be
  // distinguished in the UI (and later, analytics if ever wanted).
  skip: () => get().advance(),

  exit: () => set({ active: null, stepIndex: 0 }),
}));
